/**
 * Volumetric nebula background — fullscreen clip-space quad.
 * Based on ShaderToy MslGWN Simplicity Galaxy fractal orbit trap.
 *
 * Architecture: A 2x2 plane rendered directly in clip space (bypassing all
 * model/view/projection transforms). Every pixel evaluates the fractal.
 * No world-space UV remapping, no vignette, no frustum culling issues.
 *
 * Two fractal layers at different 3D positions create internal parallax depth.
 * Camera XZ position drives a subtle drift in fractal space for parallax.
 * Time-varying strength and color breathing create a living, evolving galaxy.
 * Zoom responds to camera Y height for subtle background zoom effect.
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '../../stores/gameStore.js';
import { TICK_DELTA } from '@warmancer/shared';

function fract(x: number): number {
  return x - Math.floor(x);
}

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.9999, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec2 uCameraWorldPos;
  uniform float uTime;
  uniform vec2 uResolution;
  uniform vec3 uSeed;
  uniform float uZoom;
  varying vec2 vUv;

  // 26-iteration fractal orbit trap — creates galaxy-like spiral/cloud structures
  // Contrast exponent on output: 1.5 = heavy/dense clouds, 2.0 = default, 2.5 = sparse with more black space.
  // Tune per map theme. Currently hardcoded; future: derive from map config.
  float field(in vec3 p, float s) {
    float strength = 7.0 + 0.08 * sin(uTime * 0.05);
    float accum = s / 4.0;
    float prev = 0.0;
    float tw = 0.0;
    for (int i = 0; i < 26; ++i) {
      float mag = dot(p, p);
      p = abs(p) / mag + vec3(-0.5, -0.4, -1.5);
      float w = exp(-float(i) / 7.0);
      accum += w * exp(-strength * pow(abs(mag - prev), 2.2));
      tw += w;
      prev = mag;
    }
    return pow(max(0.0, 5.0 * accum / tw - 0.7), 2.0);
  }

  // 18-iteration version — cheaper second layer
  float field2(in vec3 p, float s) {
    float strength = 7.0 + 0.08 * sin(uTime * 0.05);
    float accum = s / 4.0;
    float prev = 0.0;
    float tw = 0.0;
    for (int i = 0; i < 18; ++i) {
      float mag = dot(p, p);
      p = abs(p) / mag + vec3(-0.5, -0.4, -1.5);
      float w = exp(-float(i) / 7.0);
      accum += w * exp(-strength * pow(abs(mag - prev), 2.2));
      tw += w;
      prev = mag;
    }
    return pow(max(0.0, 5.0 * accum / tw - 0.7), 2.0);
  }

  // Procedural star hash — two layers of cos/sin for uniform dense coverage
  vec3 nrand3(vec2 co) {
    vec3 a = fract(cos(co.x * 8.3e-3 + co.y) * vec3(1.3e5, 4.7e5, 2.9e5));
    vec3 b = fract(sin(co.x * 0.3e-3 + co.y) * vec3(8.1e5, 1.0e5, 0.1e5));
    return mix(a, b, 0.5);
  }

  void main() {
    // UV math matching original ShaderToy exactly (single aspect correction)
    vec2 uv = (vUv - 0.5) * 2.0;
    vec2 uvs = uv * uResolution.xy / max(uResolution.x, uResolution.y);

    // Zoom: camera height scales fractal UVs
    float zoomScale = uZoom / 50.0;
    uvs *= mix(1.0, sqrt(zoomScale), 0.3);

    // Color breathing (no positional motion)
    float f0 = (0.35 + 0.10 * uSeed.x) + 0.05 * sin(uTime * 0.13);
    float f1 = (0.40 + 0.10 * uSeed.y) + 0.05 * sin(uTime * 0.17);
    float f2 = (0.45 + 0.10 * uSeed.z) + 0.05 * sin(uTime * 0.11);
    float f3 = (0.35 + 0.10 * fract(uSeed.x + uSeed.y)) + 0.05 * sin(uTime * 0.19);

    // Parallax: uCameraWorldPos is pre-projected into screen space (JS side),
    // so drift.x = screen horizontal, drift.y = screen vertical. No axis swap needed.
    vec2 drift = uCameraWorldPos * 0.0005;

    // Primary layer — original divisor /4.0
    // z-drift uses sin() to stay bounded (linear drift breaks at large uTime values
    // because the fractal orbit trap produces zero when z >> 2)
    vec3 p = vec3(uvs / 4.0, 0.0) + vec3(1.0, -1.3, 0.0) + uSeed;
    p.xy += drift;
    p.z += sin(uTime * 0.004);

    float t = field(p, f2);

    // Second layer — constant divisor /4.6 (no zoom oscillation)
    vec3 p2 = vec3(uvs / 4.6, 1.5) + vec3(2.0, -1.3, -1.0) + uSeed * 0.7;
    p2.xy += drift * 1.3;
    p2.z += sin(uTime * 0.003);

    float t2 = field2(p2, f3);
    vec4 c2 = vec4(1.3 * t2 * t2 * t2, 1.8 * t2 * t2, t2 * f0, t2);

    // Procedural inline stars (two layers, matching fractal parallax)
    vec2 seed = p.xy * 2.0;
    seed = floor(seed * uResolution.x);
    vec3 rnd = nrand3(seed);
    vec4 starcolor = vec4(pow(rnd.y, 40.0));

    vec2 seed2 = p2.xy * 2.0;
    seed2 = floor(seed2 * uResolution.x);
    vec3 rnd2 = nrand3(seed2);
    starcolor += vec4(pow(rnd2.y, 40.0));

    vec3 ambient = vec3(0.008, 0.005, 0.015);
    vec3 nebulaColor = vec3(1.5 * f2 * t * t * t, 1.2 * f1 * t * t, f3 * t) + c2.rgb;
    gl_FragColor = vec4(ambient + nebulaColor + starcolor.rgb, 1.0);
  }
`;

export function VolumetricNebula() {
  const meshRef = useRef<THREE.Mesh>(null);

  const worldSetup = useGameStore((s) => s.worldSetup);
  const serverTick = useGameStore((s) => s.serverTick);

  // Derive seed from planet params — deterministic per location
  const seed = useMemo(() => {
    if (!worldSetup) return new THREE.Vector3(0.5, 0.5, 0.5);
    return new THREE.Vector3(
      fract(worldSetup.planet.mass * 0.1),
      fract(worldSetup.planet.radius * 0.3),
      fract(worldSetup.moon.orbitSpeed * 7.0),
    );
  }, [worldSetup]);

  const uniforms = useMemo(
    () => ({
      uCameraWorldPos: { value: new THREE.Vector2(0, 0) },
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(1920, 1080) },
      uSeed: { value: seed },
      uZoom: { value: 50 },
    }),
    [],
  );

  useFrame((state) => {
    // Derive uTime directly from serverTick every frame.
    // All clients receive the same serverTick from the Durable Object,
    // so the nebula fractal is always identical between clients.
    // No accumulation drift from tab switches or frame-rate differences.
    uniforms.uTime.value = serverTick * TICK_DELTA * 0.75;

    // Update seed when worldSetup changes
    uniforms.uSeed.value = seed;

    // Project camera world position into screen-aligned coordinates.
    // The camera rotates with ship heading (camera.up = (cosθ, 0, sinθ)),
    // so world X/Z don't align with screen horizontal/vertical.
    // Screen right in world space = (-sinθ, 0, cosθ) = (-up.z, 0, up.x)
    // Screen up in world space = (cosθ, 0, sinθ) = (up.x, 0, up.z)
    const camX = state.camera.position.x;
    const camZ = state.camera.position.z;
    const upX = state.camera.up.x;
    const upZ = state.camera.up.z;
    const screenX = -camX * upZ + camZ * upX;
    const screenY = camX * upX + camZ * upZ;
    uniforms.uCameraWorldPos.value.set(screenX, screenY);

    // Camera Y for zoom response
    uniforms.uZoom.value = state.camera.position.y;

    // Update resolution for aspect ratio
    uniforms.uResolution.value.set(state.size.width, state.size.height);
  });

  return (
    <mesh ref={meshRef} frustumCulled={false} renderOrder={-1000}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        depthTest={false}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}
